import { Locale } from './constants/default-locales';
import { Converter } from '@app/shared/utils/converter';
import { EnvironmentType } from './constants/environment-type';

interface SnowboundConfig {
  viewerUrl: string;
  supportedFileTypes: string;
}

interface SharePointConfig {
  supportedFileTypes: string;
}

interface OnlyOffice {
  supportedFileTypes: string;
  supportedViewerFileTypes: string;
}

interface SnowboundPDFConverterConfig {
  supportedFileTypes: string;
}

interface DocuSignConfig {
  supportedFileTypes: string;
}

interface PleaseReviewConfig {
  allowedFileTypes: string;
}

interface ARenderConfig {
  supportedFileTypes: string[];
}

interface LMSListingConfig {
  Name: string;
  Url: string;
}

interface LmsConfig {
  DomainLMSName: string;
  DefaultLMSName: string;
  LMSListing: LMSListingConfig[];
}

interface QuickSightConfig {
  enabled: boolean;
}

interface UserGuide {
  domainSA: string;
  domainNonSA: string;
  roomSA: string;
  roomAdmin: string;
  roomEditor: string;
  roomReader: string;
}

interface ServerConfig {
  sessionTimeout: number;
  sessionPingTimeout: number;
  appMonitoringToken: string;
  appAnalyticsToken: string;
  openPanelUrl: string;
  openPanelClientId: string;
  disabledFeatures: any;
  enabledFeatures: any;
  userLookupMinRoleLevel: number;
  restrictedFileExtensions: string;
  maintenanceIsScheduled: boolean;
  maintenanceIsActive: boolean;
  snowboundViewer: SnowboundConfig;
  sharepoint: SharePointConfig;
  snowboundPDFConverter: SnowboundPDFConverterConfig;
  docuSign: DocuSignConfig;
  pleaseReview: PleaseReviewConfig;
  aRender: ARenderConfig;
  docTypeAutoDetectDisabled: boolean;
  supportedRecognitionLanguages: string[];
  centralAuthentication: string;
  jasperReportsUrl: string;
  jasperReportsKpiDashboardUrl: string;
  lmsconfig: LmsConfig;
  allowedUserProfilePictures: string;
  allowedUserProfilePictureExtensions: string;
  homePage: string;
  inboxServer: string;
  onlyOffice: OnlyOffice;
  MyTiUserAgentPrefix?: string;
  alfrescoSupportedThumbnailFileTypes?: string[];
  userGuide: UserGuide;
  transPortAllowedFileExtensions: string;
  sentryUILogDSN: string;
  sentryUITargets?: string[];
  tracesSampleRate?: string;
  sentryEnvironment?: string;
  quickSight?: QuickSightConfig; // TODO: remove
  loginUrl: string;
  serverUrl: string;
}

interface MainConfig {
  companyName: string;
  shortName: string;
  supportEmail: string;
  lmsSupportEmail: string;
  logoUrl: string;
  domain: string;
  defaultLocale: Locale;
  isMultitenantInstance: boolean;
  mobileAccessEnabled: boolean;
  environmentType: EnvironmentType;
  serverCfg: ServerConfig;
  richTextEditorLicenseKey: string;
  notificationTimeout: number;
  EmailValidationRegexpPattern: string;
  EmailValidationRegexp: RegExp;
  PasswordValidationMessage: string;
  UseValidationMessageSetting: boolean;
  PasswordValidationRegexpPattern: string;
  PasswordValidationRegexp: RegExp;
  chunkFileUploadSettings: {
    ChunkSizeKb: number;
    AllowParallelUpload: false;
    BatchUploadSize: 5;
  };
  ServerTimeZone: number;
  IamEnabled: boolean;
}

let config: MainConfig;

export function getServerConfig(): MainConfig {
  if (!config || window['TIUnitTests']) {
    delete window['TIUnitTests'];

    const TI = window['TI'] || {};
    config = TI.branding || <MainConfig>{};
    config.domain = config.domain || null;
    config.companyName = config.companyName || 'Trial Interactive';
    config.supportEmail = config.supportEmail || 'help@trialinteractive.com';
    config.lmsSupportEmail = config.lmsSupportEmail || 'help@trialinteractive.com';

    config.notificationTimeout = config.notificationTimeout || 3000;

    config.EmailValidationRegexp = new RegExp(config.EmailValidationRegexpPattern || '^[^@]+@[^@.]+.[^@.]{2,}$', 'i');
    config.PasswordValidationRegexp = new RegExp(
      config.PasswordValidationRegexpPattern || '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[*.%$!@#\\-])(?!.*\\s).{8,}$',
    );
    config.UseValidationMessageSetting = !!config.PasswordValidationMessage;
    config.serverCfg = config.serverCfg || <any>{};

    const serverCfg = config.serverCfg;
    serverCfg.snowboundViewer = serverCfg.snowboundViewer || <any>{};
    serverCfg.disabledFeatures =
      (serverCfg.disabledFeatures || '') instanceof Array
        ? serverCfg.disabledFeatures
        : (serverCfg.disabledFeatures || '')
            .split(',')
            .filter((item) => item.trim() !== '')
            .map((item) => item.toLowerCase().trim());

    serverCfg.enabledFeatures =
      (serverCfg.enabledFeatures || '') instanceof Array
        ? serverCfg.enabledFeatures
        : (serverCfg.enabledFeatures || '')
            .split(',')
            .filter((item) => item.trim() !== '')
            .map((item) => item.toLowerCase().trim());

    serverCfg.sharepoint = serverCfg.sharepoint || <any>{};
    serverCfg.snowboundPDFConverter = serverCfg.snowboundPDFConverter || <any>{};
    serverCfg.docuSign = serverCfg.docuSign || <any>{};
    serverCfg.pleaseReview = serverCfg.pleaseReview || <any>{};
    serverCfg.lmsconfig = serverCfg.lmsconfig || <any>{};
    serverCfg.onlyOffice = serverCfg.onlyOffice || <any>{};
    serverCfg.userGuide = serverCfg.userGuide || <any>{};
    serverCfg.MyTiUserAgentPrefix;
    config.richTextEditorLicenseKey = config.richTextEditorLicenseKey || '';
    serverCfg.alfrescoSupportedThumbnailFileTypes = ((serverCfg.alfrescoSupportedThumbnailFileTypes || '') as string)
      .toUpperCase()
      .split(',');
    serverCfg.aRender = serverCfg.aRender || <any>{};
    serverCfg.aRender.supportedFileTypes = ((serverCfg.aRender?.supportedFileTypes || '') as string)
      .toUpperCase()
      .split(',');
    serverCfg.transPortAllowedFileExtensions = serverCfg.transPortAllowedFileExtensions || <any>{};
    serverCfg.sentryUILogDSN = serverCfg.sentryUILogDSN || '';
    serverCfg.sentryUITargets = ((serverCfg.sentryUITargets || '') as string).split(',');
    serverCfg.openPanelUrl = serverCfg.openPanelUrl || '';
    serverCfg.openPanelClientId = serverCfg.openPanelClientId || '';
  }

  try {
    if (tiStrings && !config.PasswordValidationMessage) {
      config.PasswordValidationMessage =
        Converter.Strings.htmlDecode(config.PasswordValidationMessage) ||
        tiStrings.tiComponents.groupsAndUsers.userActions.resetPassword.thePasswordMustHaveAtLeastOneNumber;
    }
  } catch (ex) {}

  return config;
}
